import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
} from "firebase/auth";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "react-hot-toast";
import { auth } from "./backend/firebaseConfig";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const Index = () => {
  const provider = new GoogleAuthProvider();
  const [login, setLogin] = useState(false);
  const navigate = useNavigate();

  // SEO Data and Helper Functions
  const seoData = {
    title:
      "Link Forest - Free Bio Link Tool | Connect All Your Links in One Place",
    description:
      "Create your professional bio link page with Link Forest. Free tool for creators, influencers, and businesses to share all important links in one place. Get started in minutes!",
    keywords:
      "bio link, link in bio, social media links, link tree alternative, link forest, creator tools, influencer marketing, social media marketing, free bio link, personal branding, link management, social media bio",
    url: window.location.origin,
    image: `${window.location.origin}/favicon.ico`,
    siteName: "Link Forest",
    author: "Krish Jotaniya",
  };

  const generateStructuredData = () => {
    const structuredData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebSite",
          "@id": `${seoData.url}/#website`,
          url: seoData.url,
          name: seoData.siteName,
          description: seoData.description,
          publisher: {
            "@id": `${seoData.url}/#organization`,
          },
          potentialAction: [
            {
              "@type": "SearchAction",
              target: {
                "@type": "EntryPoint",
                urlTemplate: `${seoData.url}/{search_term_string}`,
              },
              "query-input": "required name=search_term_string",
            },
          ],
          inLanguage: "en-US",
        },
        {
          "@type": "Organization",
          "@id": `${seoData.url}/#organization`,
          name: seoData.siteName,
          url: seoData.url,
          logo: {
            "@type": "ImageObject",
            url: seoData.image,
            contentUrl: seoData.image,
            width: 512,
            height: 512,
            caption: seoData.siteName,
          },
          description: seoData.description,
          founder: {
            "@type": "Person",
            name: seoData.author,
          },
          sameAs: [
            "https://www.producthunt.com/posts/link-forest",
            "https://play.google.com/store/apps/details?id=com.krish.linkforest",
            "https://krishjotaniya.netlify.app/",
          ],
        },
        {
          "@type": "WebPage",
          "@id": `${seoData.url}/#webpage`,
          url: seoData.url,
          name: seoData.title,
          isPartOf: {
            "@id": `${seoData.url}/#website`,
          },
          description: seoData.description,
          breadcrumb: {
            "@id": `${seoData.url}/#breadcrumb`,
          },
          inLanguage: "en-US",
        },
        {
          "@type": "BreadcrumbList",
          "@id": `${seoData.url}/#breadcrumb`,
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: seoData.url,
            },
          ],
        },
        {
          "@type": "SoftwareApplication",
          name: "Link Forest",
          applicationCategory: "BusinessApplication",
          applicationSubCategory: "Social Media Management",
          operatingSystem: "Web, Android",
          description:
            "Free bio link tool that helps creators, influencers, and businesses connect all their important links in one place. Create beautiful, customizable bio link pages in minutes.",
          featureList: [
            "Custom bio link pages",
            "Multiple themes",
            "Social media integration",
            "Analytics tracking",
            "Mobile responsive",
            "Free forever",
          ],
        },
      ],
    };

    return JSON.stringify(structuredData);
  };
  useEffect(() => {
    onAuthStateChanged(auth, (user) => {
      if (!user) {
        setLogin(false);
      } else {
        setLogin(true);
      }
    });
  }, []);

  const googleLoginEventHandler = () => {
    toast.loading("Logging In");
    signInWithPopup(auth, provider)
      .then((result) => {
        toast.dismiss();
        toast.success("Login Successful");
        setLogin(true);
        navigate("/dashboard");
      })
      .catch((error) => {
        toast.dismiss();
        console.log(error);
        toast.error("Login Failed");
      });
  };

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <meta name="author" content={seoData.author} />
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />
        <meta name="language" content="en" />
        <meta name="revisit-after" content="7 days" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />

        {/* Canonical URL */}
        <link rel="canonical" href={seoData.url} />

        {/* Open Graph Meta Tags */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:url" content={seoData.url} />
        <meta property="og:site_name" content={seoData.siteName} />
        <meta property="og:image" content={seoData.image} />
        <meta
          property="og:image:alt"
          content="Link Forest - Free Bio Link Tool"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta name="twitter:image" content={seoData.image} />
        <meta
          name="twitter:image:alt"
          content="Link Forest - Free Bio Link Tool"
        />
        <meta name="twitter:site" content={seoData.twitterHandle} />

        {/* Additional SEO Meta Tags */}
        <meta name="theme-color" content="#10b981" />
        <meta name="msapplication-TileColor" content="#10b981" />
        <meta name="format-detection" content="telephone=no" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="apple-mobile-web-app-title" content="Link Forest" />

        {/* Geo and Location Tags */}
        <meta name="geo.region" content="Global" />
        <meta name="geo.placename" content="Worldwide" />

        {/* Business and Category Tags */}
        <meta name="category" content="Business, Technology, Social Media" />
        <meta name="coverage" content="Worldwide" />
        <meta
          name="target"
          content="creators, influencers, businesses, content creators, social media managers"
        />

        {/* App Store Meta Tags */}
        <meta name="apple-itunes-app" content="app-id=com.krish.linkforest" />
        <meta name="google-play-app" content="app-id=com.krish.linkforest" />

        {/* Structured Data */}
        <script type="application/ld+json">{generateStructuredData()}</script>

        {/* Preconnect for performance */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin="anonymous"
        />
        <link rel="preconnect" href="https://res.cloudinary.com" />
        <link rel="preconnect" href="https://www.youtube.com" />
        <link rel="preconnect" href="https://api.producthunt.com" />
        <link rel="preconnect" href="https://play.google.com" />

        {/* DNS Prefetch */}
        <link rel="dns-prefetch" href="//www.youtube.com" />
        <link rel="dns-prefetch" href="//api.producthunt.com" />
        <link rel="dns-prefetch" href="//play.google.com" />

        {/* Additional rich snippets support */}
        <meta property="product:brand" content="Link Forest" />
        <meta property="product:availability" content="in stock" />
        <meta property="product:condition" content="new" />
        <meta property="product:price:amount" content="0" />
        <meta property="product:price:currency" content="USD" />
      </Helmet>

      <div className="min-h-screen w-full">
        <header className="mt-16 sm:mt-20 md:mt-28 flex justify-center items-center flex-col w-full px-4">
          <motion.h1
            initial={{
              opacity: 0,
              y: 50,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-Montserrat uppercase text-center font-bold tracking-wide text-slate-950 w-full max-w-6xl"
          >
            Link <span className="text-emerald-500">Forest</span>
          </motion.h1>
          <motion.p
            initial={{
              opacity: 0,
              y: 50,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            className="mt-4 sm:mt-6 md:mt-8 font-Montserrat font-medium text-center text-base sm:text-lg md:text-xl lg:text-2xl w-full max-w-4xl px-4 text-slate-600 leading-relaxed"
          >
            Create your bio link page in minutes. Connect all your social media
            profiles, websites, and important links in one beautiful page that's{" "}
            <span className="text-emerald-600 font-semibold">free forever</span>
            .
          </motion.p>
          <div className="mt-6 sm:mt-8 md:mt-10">
            {!login && (
              <button
                className="text-slate-950 flex justify-center items-center font-Montserrat px-4 sm:px-6 py-3 rounded mx-auto tracking-wide text-base sm:text-lg ease-linear hover:ease-linear transition-all hover:transition-all duration-300 hover:duration-300 font-semibold bg-emerald-500 hover:bg-emerald-600"
                onClick={googleLoginEventHandler}
                aria-label="Create your free bio link page with Link Forest"
              >
                Create Yours Now!
              </button>
            )}
            {login && (
              <button
                className="text-slate-50 bg-slate-950 flex justify-center items-center font-Montserrat px-4 sm:px-6 py-3 rounded mx-auto tracking-wide text-base sm:text-lg ease-linear hover:ease-linear transition-all hover:transition-all duration-300 hover:duration-300 font-semibold hover:bg-slate-800"
                onClick={() => navigate("/dashboard")}
                aria-label="Go to your Link Forest dashboard"
              >
                Go To Dashboard
              </button>
            )}
          </div>
        </header>
        <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* <section className="mt-8 sm:mt-12 md:mt-16 flex justify-center lg:justify-between items-center flex-col lg:flex-row gap-8 lg:gap-12">
            <motion.img
              initial={{
                opacity: 0,
                x: -50,
              }}
              animate={{
                opacity: 1,
                x: 0,
              }}
              src={require("./assets/homePageUp.png")}
              width={400}
              height={400}
              alt="Link Forest mobile app interface showing customizable themes and link management"
              className="w-[280px] h-[280px] sm:w-[320px] sm:h-[320px] md:w-[400px] md:h-[400px] lg:w-[450px] lg:h-[450px] flex-shrink-0"
            />
            <motion.div
              initial={{
                opacity: 0,
                x: 50,
              }}
              animate={{
                opacity: 1,
                x: 0,
              }}
              className="flex-1 flex justify-center lg:justify-start"
            >
              <h2 className="font-Montserrat font-extrabold text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl text-center lg:text-left uppercase lg:capitalize leading-tight lg:leading-none tracking-wider lg:tracking-normal">
                Create
                <br />
                Bio
                <br />
                Links.
              </h2>
            </motion.div>
          </section> */}
          <section className="mt-12 sm:mt-16 md:mt-20 lg:mt-24 flex justify-center lg:justify-between items-center flex-col lg:flex-row-reverse gap-8 lg:gap-12">
            <motion.img
              initial={{
                opacity: 0,
                x: -50,
              }}
              animate={{
                opacity: 1,
                x: 0,
              }}
              src={require("./assets/homeDown.png")}
              width={400}
              height={400}
              alt="Link Forest bio link creation interface - Create professional bio link pages"
              className="w-[280px] h-[280px] sm:w-[320px] sm:h-[320px] md:w-[400px] md:h-[400px] lg:w-[450px] lg:h-[450px] flex-shrink-0"
            />
            <motion.div
              initial={{
                opacity: 0,
                x: 50,
              }}
              animate={{
                opacity: 1,
                x: 0,
              }}
              className="flex-1 text-center lg:text-left"
            >
              <h3 className="font-Montserrat font-medium text-lg sm:text-xl md:text-2xl lg:text-3xl">
                Unify your online presence with Link Forest
              </h3>
              <ul className="list-none space-y-3 sm:space-y-4 mt-4 sm:mt-6">
                <li className="font-Montserrat font-medium text-base sm:text-lg md:text-xl">
                  Create Your's In Just Minutes.
                </li>
                <li className="font-Montserrat font-medium text-base sm:text-lg md:text-xl">
                  Different Themes And Minimal Looks.
                </li>
                <li className="font-Montserrat font-medium text-base sm:text-lg md:text-xl">
                  And Its Free Forever 100%
                </li>
              </ul>
              <div className="w-full flex justify-center lg:justify-start items-center mt-6 sm:mt-8">
                <a
                  href="https://www.producthunt.com/posts/link-forest?utm_source=badge-featured&utm_medium=badge&utm_souce=badge-link&#0045;forest"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Link Forest featured on Product Hunt"
                >
                  <img
                    src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=373167&theme=light"
                    alt="Link Forest featured on Product Hunt - Your one-stop hub for all your social and important links"
                    width="180"
                    height="45"
                    className="w-36 sm:w-44 md:w-48"
                  />
                </a>
              </div>
            </motion.div>
          </section>

          <section className="mt-12 sm:mt-16 md:mt-20 lg:mt-24">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-Montserrat font-bold text-slate-950 mb-4 sm:mb-6">
                Why Choose{" "}
                <span className="text-emerald-500">Link Forest?</span>
              </h2>
              <p className="text-lg sm:text-xl md:text-2xl font-Montserrat text-slate-700 max-w-3xl mx-auto">
                Everything you need to manage your online presence in one place
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                  <svg
                    className="w-6 h-6 text-emerald-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold font-Montserrat text-slate-950 mb-3">
                  Multiple Links in One Place
                </h3>
                <p className="text-slate-600 font-Montserrat leading-relaxed">
                  Consolidate all your important links, social profiles, and
                  websites into a single, beautiful bio link page.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                  <svg
                    className="w-6 h-6 text-emerald-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5H9a2 2 0 00-2 2v10a4 4 0 004 4h6a2 2 0 002-2V7a2 2 0 00-2-2z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold font-Montserrat text-slate-950 mb-3">
                  Beautiful Themes
                </h3>
                <p className="text-slate-600 font-Montserrat leading-relaxed">
                  Choose from 6 stunning pre-made themes or create your own
                  custom design with unlimited color options.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-white rounded-xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                  <svg
                    className="w-6 h-6 text-emerald-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold font-Montserrat text-slate-950 mb-3">
                  Detailed Analytics
                </h3>
                <p className="text-slate-600 font-Montserrat leading-relaxed">
                  Track your profile views, link clicks, and social media
                  engagement with comprehensive analytics.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="bg-white rounded-xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                  <svg
                    className="w-6 h-6 text-emerald-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold font-Montserrat text-slate-950 mb-3">
                  Mobile Optimized
                </h3>
                <p className="text-slate-600 font-Montserrat leading-relaxed">
                  Fully responsive design works perfectly on all devices.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="bg-white rounded-xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                  <svg
                    className="w-6 h-6 text-emerald-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold font-Montserrat text-slate-950 mb-3">
                  8 Social Platforms
                </h3>
                <p className="text-slate-600 font-Montserrat leading-relaxed">
                  Connect Instagram, LinkedIn, GitHub, Twitter/X, Facebook,
                  Threads, Email, and Snapchat in one place.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="bg-white rounded-xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 md:col-span-2 lg:col-span-1"
              >
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                  <svg
                    className="w-6 h-6 text-emerald-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold font-Montserrat text-slate-950 mb-3">
                  Free Forever
                </h3>
                <p className="text-slate-600 font-Montserrat leading-relaxed">
                  100% free with no hidden costs. Create unlimited links, use
                  all themes, and get full analytics at no charge.
                </p>
              </motion.div>
            </div>

            <div className="text-center">
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-Montserrat font-bold text-slate-950 mb-4 sm:mb-6">
                Ready to Create Your Link Forest?
              </h3>
              <p className="text-lg sm:text-xl font-Montserrat text-slate-700 mb-6 sm:mb-8 max-w-2xl mx-auto">
                Join thousands of creators, influencers, and businesses who
                trust Link Forest for their bio link needs.
              </p>
              {!login && (
                <button
                  className="text-slate-950 flex justify-center items-center font-Montserrat px-8 py-4 rounded-lg mx-auto tracking-wide text-lg font-semibold bg-emerald-500 hover:bg-emerald-600 transition-colors duration-300 shadow-lg hover:shadow-xl"
                  onClick={googleLoginEventHandler}
                  aria-label="Start creating your Link Forest bio link page"
                >
                  Start Building Your Link Forest
                </button>
              )}
              {login && (
                <button
                  className="text-slate-50 bg-slate-950 flex justify-center items-center font-Montserrat px-8 py-4 rounded-lg mx-auto tracking-wide text-lg font-semibold hover:bg-slate-800 transition-colors duration-300 shadow-lg hover:shadow-xl"
                  onClick={() => navigate("/dashboard")}
                  aria-label="Go to your Link Forest dashboard"
                >
                  Go To Your Dashboard
                </button>
              )}
            </div>
          </section>
        </main>
        <footer
          className="mt-8 sm:mt-10 md:mt-12 mb-2 w-full cursor-pointer px-4"
          onClick={() => window.open("https://krishjotaniya.netlify.app/")}
        >
          <p className="font-Montserrat text-sm sm:text-base md:text-lg font-semibold py-3 text-center">
            Developed With ❤️ By <span itemProp="author">Krish Jotaniya</span>
          </p>
        </footer>
      </div>
    </>
  );
};

export default Index;
